// Pieces of the Deposit tab, drawn from the values of useDeposit.ts: the
// address, the QR on its plate, the wallet the address is of and the choice
// of it. The tab places them; it does not make them again.

import {
  CaretDownIcon,
  CheckIcon,
  CopyIcon,
  LockSimpleIcon,
  QrCodeIcon,
} from '@phosphor-icons/react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { UserlandWallet } from '../../api/userland'
import { formatTon, useLiveWalletBalance } from '../../hooks/useLiveWalletBalance'
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss'
import { shortAddr } from '../../lib/format'
import { addressToFriendlyForm } from '../../lib/tonRawAddress'
import soft from '../../styles/soft.module.css'
import { Skeleton } from '../common/Skeleton'
import { Described } from '../ui/Described'
import { useCopyToast } from '../ui/copyToast'
import { GramMark } from '../ui/icons/GramMark'
import { PrimaryStar } from '../wallet/WalletParts'
import { DepositQr } from './DepositQr'
import styles from './exchange.module.css'
import type { AddressGroup, Deposit } from './useDeposit'

/** The address, as plain text of normal size that wraps. One hidden text node
 *  holds it whole — what a screen reader says once. What is seen are its
 *  pieces of four set tight, with nothing between them: they read as one
 *  string and wrap anywhere, and the first and the last are stressed so they
 *  can be checked by eye. A selection dragged over them copies the address
 *  with nothing between. Never cut, never abbreviated. */
export function AddressText({
  address,
  groups,
  className,
}: {
  address: string
  groups: AddressGroup[]
  className?: string
}) {
  return (
    <>
      <span className={styles.addressValue}>{address}</span>
      <span className={`${styles.addressGroups} ${className ?? ''}`} aria-hidden="true">
        {groups.map((g, gi) => (
          <span key={g.at} data-end={gi === 0 || gi === groups.length - 1 ? 'true' : undefined}>
            {g.text}
          </span>
        ))}
      </span>
    </>
  )
}

/** The QR on its white plate — or, with nothing to say, a quiet plate that
 *  nothing can scan (while the wallet list is still coming, the same plate,
 *  breathing). Keyed by what it says: another address is another drawing,
 *  never the previous one redrawn. `className` sizes the plate. */
export function QrPlate({
  text,
  loading = false,
  className,
}: {
  text: string | null
  loading?: boolean
  className?: string
}) {
  return text ? (
    <span className={`${styles.qr} ${className ?? ''}`}>
      <DepositQr key={text} text={text} className={styles.qrSvg} />
    </span>
  ) : (
    <span className={`${styles.qrNone} ${className ?? ''}`} data-loading={loading || undefined}>
      <QrCodeIcon size={36} weight="regular" aria-hidden="true" />
    </span>
  )
}

const walletName = (wallet: UserlandWallet) =>
  wallet.label || (wallet.is_primary ? 'Primary wallet' : 'Wallet')

/** The primary wallet's mark: the money windows' gold star (round 9, the
 *  operator: "a star instead of the dot"). Named, not worded: the owner's
 *  text stays the wallet's name alone. */
function PrimaryMark() {
  return <PrimaryStar size={12} />
}

/** Whose address it is: the wallet's name (cut by its box, whole in its
 *  text) and, on the primary, its gold star. */
export function WalletOwner({ wallet, className }: { wallet: UserlandWallet; className?: string }) {
  const name = walletName(wallet)
  return (
    <span className={`${styles.owner} ${className ?? ''}`}>
      <span className={styles.ownerName}>{name}</span>
      {wallet.is_primary ? <PrimaryMark /> : null}
    </span>
  )
}

// MONEY PATH. A wallet's deposit address as the tab would show it, copy it
// and put it in its QR once that wallet is chosen: useDeposit's derivation,
// the same expression (the non-bounceable UQ form; a stored address the
// converter does not take, as it is stored). The menu's rows show it short
// and copy it whole — the string the tab would, never another form.
const depositAddressOf = (wallet: UserlandWallet) =>
  addressToFriendlyForm(wallet.address, false) ?? wallet.address

/** Whose address it is, and — with more than one live wallet — the choice of
 *  it: the column's foot is then a select as wide as the address over it
 *  (the name, the primary's star, the caret at its end) that opens a Soft
 *  menu of the wallets: the name and the start and end of its deposit
 *  address, its GRAM, a copy of that address; the star on the primary, the
 *  check on the one shown. With one wallet it is the plain owner: nothing to
 *  pick, nothing pressable. Choosing only changes what the tab shows
 *  (useDeposit): no wallet changed. The rows read each wallet's balance —
 *  the read Portfolio's Wallets makes for its rows, nothing written. */
export function DepositWallet({
  deposit,
  className,
}: {
  deposit: Pick<Deposit, 'wallet' | 'choices' | 'choose'>
  className?: string
}) {
  const { wallet, choices, choose } = deposit
  if (!wallet) return null
  if (choices.length < 2) return <WalletOwner wallet={wallet} className={className} />
  return <WalletPicker wallet={wallet} choices={choices} choose={choose} className={className} />
}

// the menu hangs from the select's right end, under it — or over it when the
// window has no room below. Fixed to the window: the dialog's surface clips
// and its body scrolls, and a menu must not be cut by either.
const MENU_GAP = 8
const MENU_ROW = 50
const MENU_PAD = 8
const MENU_MAX_ROWS = 5
function menuPlace(trigger: HTMLElement, rows: number) {
  const r = trigger.getBoundingClientRect()
  const height = Math.min(rows, MENU_MAX_ROWS) * MENU_ROW + MENU_PAD
  const right = Math.max(8, window.innerWidth - r.right)
  const below = window.innerHeight - r.bottom - MENU_GAP
  return below >= height || below >= r.top - MENU_GAP
    ? { top: r.bottom + MENU_GAP, right }
    : { bottom: window.innerHeight - r.top + MENU_GAP, right }
}

function WalletPicker({
  wallet,
  choices,
  choose,
  className,
}: {
  wallet: UserlandWallet
  choices: UserlandWallet[]
  choose: (id: string) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<{ top?: number; bottom?: number; right: number } | null>(null)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  usePopoverDismiss(wrapRef, open, () => setOpen(false), btnRef)
  // Escape with the menu open is the menu's (TokenSelect's manner): the hook
  // above shuts it, and the key's default — the native dialog's own close —
  // is cancelled. With the menu shut the key is the dialog's.
  useEffect(() => {
    if (!open) return
    const keepDialog = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault()
    }
    // the menu stands fixed to the window: a scroll under it would leave it
    // behind, so a scroll shuts it — but not the menu's own scroll, past five
    // wallets (a capture listener hears every scrolling box)
    const shut = () => setOpen(false)
    const shutOnScroll = (e: Event) => {
      if (e.target instanceof Node && wrapRef.current?.contains(e.target)) return
      shut()
    }
    document.addEventListener('keydown', keepDialog, true)
    window.addEventListener('scroll', shutOnScroll, true)
    window.addEventListener('resize', shut)
    return () => {
      document.removeEventListener('keydown', keepDialog, true)
      window.removeEventListener('scroll', shutOnScroll, true)
      window.removeEventListener('resize', shut)
    }
  }, [open])
  useLayoutEffect(() => {
    if (open && btnRef.current) setPlace(menuPlace(btnRef.current, choices.length))
  }, [open, choices.length])

  const name = walletName(wallet)
  return (
    <span className={`${styles.pickWrap} ${className ?? ''}`} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.owner} ${styles.ownerPick}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Deposit wallet: ${name}`}
        data-open={open || undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.ownerName}>{name}</span>
        {wallet.is_primary ? <PrimaryMark /> : null}
        <CaretDownIcon size={12} weight="bold" className={styles.ownerCaret} aria-hidden="true" />
      </button>
      {open && place ? (
        <div
          className={`${soft.menu} ${styles.walletMenu}`}
          role="menu"
          aria-label="Deposit wallet"
          style={{ top: place.top, bottom: place.bottom, right: place.right }}
          data-drop={place.bottom !== undefined ? 'up' : 'down'}
        >
          {choices.map((w) => (
            <WalletMenuRow
              key={w.id}
              name={walletName(w)}
              primary={w.is_primary}
              address={depositAddressOf(w)}
              gramOf={w.address}
              chosen={w.id === wallet.id}
              onChoose={() => {
                choose(w.id)
                setOpen(false)
                btnRef.current?.focus()
              }}
            />
          ))}
        </div>
      ) : null}
    </span>
  )
}

// A row of a wallet menu, as the product's other pickers mark theirs (round
// 9e, the operator: "why is the check at the left ... the copy by the address,
// as everywhere; why four and four"): the choice itself — the name and the
// star over the address's first four and last four, the GRAM, and at the
// right the accent check on the chosen one, as the token menu has it — and,
// beside the address but not in the choice (a control holds no control), the
// copy of it. The row is one grid and the choice lays its parts on the row's
// own tracks (subgrid), so the copy stands in the address's line, right after
// it. Deposit's wallet menu draws its rows with it. A wallet that cannot be
// chosen is `off` — listed, not chosen by a press, a lock in the check's place
// and the reason in its description (drawn for Withdraw's From list until round 9j
// took that list: From is the primary alone, wallet/FromPlate; no caller
// passes `off` today).
export function WalletMenuRow({
  name,
  primary,
  address,
  gramOf,
  gramNano,
  chosen,
  off = false,
  title,
  onChoose,
}: {
  name: string
  primary: boolean
  /** what the row shows (short) and copies (whole) */
  address: string
  /** the stored address whose GRAM the row reads */
  gramOf: string
  /** a GRAM already held for this wallet; omitted, the row reads its own */
  gramNano?: string | null
  chosen: boolean
  off?: boolean
  title?: string
  onChoose: () => void
}) {
  return (
    <div
      className={styles.walletRow}
      role="presentation"
      data-chosen={chosen || undefined}
      data-off={off || undefined}
    >
      <Described
        as="button"
        type="button"
        role="menuitemradio"
        aria-checked={chosen}
        aria-disabled={off || undefined}
        className={styles.walletPick}
        description={title}
        onClick={off ? undefined : onChoose}
      >
        <span className={styles.walletRowName}>
          <span className={styles.ownerName}>{name}</span>
          {/* a space parts the name from the star's for a reader */}
          {primary ? (
            <>
              {' '}
              <PrimaryMark />
            </>
          ) : null}
        </span>{' '}
        {/* the start and the end: four and four (the spaces part the words
            for a reader; a grid draws none) */}
        <span className={styles.walletRowAddr}>{shortAddr(address)}</span>{' '}
        <WalletGram address={gramOf} known={gramNano} />
        <span className={styles.walletCheck} aria-hidden="true">
          {chosen ? (
            <CheckIcon size={14} weight="bold" className={styles.pickerCheck} aria-hidden="true" />
          ) : off ? (
            <LockSimpleIcon
              size={13}
              weight="bold"
              className={styles.walletLock}
              aria-hidden="true"
            />
          ) : null}
        </span>
      </Described>
      <CopyAddress address={address} name={name} />
    </div>
  )
}

// A wallet's GRAM, read for its row — or the one already held for it —
// shimmering until it is read: a balance not yet read is not a zero and not a
// dash.
function WalletGram({ address, known }: { address: string; known?: string | null }) {
  const read = useLiveWalletBalance(known === undefined ? address : null)
  const balanceNano = known === undefined ? read.balanceNano : known
  if (balanceNano === null) {
    return (
      <span className={styles.walletRowBal}>
        <Skeleton width={36} height={12} />
      </span>
    )
  }
  return (
    <span className={styles.walletRowBal} data-part="wallet-gram">
      <GramMark size={12} />
      {formatTon(balanceNano)}
      <span className={styles.srOnly}> GRAM</span>
    </span>
  )
}

// The copy of one row's address, whole — the product's one copy answer
// (ui/copyToast), as the tab's own Copy: once the address is in the clipboard
// the toast says so; a refused write says nothing. The mark never changes.
function CopyAddress({ address, name }: { address: string; name: string }) {
  const copy = useCopyToast()
  return (
    <button
      type="button"
      role="menuitem"
      className={styles.walletCopy}
      aria-label={`Copy ${name} address`}
      onClick={() => copy(address, 'Deposit address copied')}
    >
      <CopyIcon size={13} weight="bold" aria-hidden="true" />
    </button>
  )
}

/** The address' place while the wallet list has not answered: lines, not the
 *  words "create a wallet". */
/** The copy said inside the dialog, for a screen reader only: the toast's
 *  stack is outside the modal dialog, where it is not read out. */
export function CopiedStatus({ text }: { text: string }) {
  return <output className={styles.srOnly}>{text}</output>
}

export function AddressLoading() {
  return (
    <span className={styles.addressLoading} aria-busy="true">
      <span className={styles.srOnly}>Loading your wallet…</span>
      <i />
      <i />
      <i />
    </span>
  )
}

/** The network, said once, in view (round 7: one line under the card in
 *  place of two — operator, 2026-10-18, on the line it replaces: "now it is
 *  really bad"). The network first, beside its mark; then what arrives. */
export const NETWORK_NOTE = 'TON network only'
export const ACCEPTS_NOTE = 'GRAM, USDT and any TON token'
